// Thẻ số liệu tổng + tab lọc trạng thái + ô tìm kiếm cho danh sách đợt thưởng.
// Lọc hoàn toàn CLIENT: API /admin-reward-program trả cả danh sách (vài chục dòng),
// không cần thêm tham số server.
import React from "react";
import { Segmented, Input, Select, Tooltip, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faBolt, faSackDollar, faUsers } from "@fortawesome/free-solid-svg-icons";
import { PROGRAM_STATUS_META, CAMP_LABELS, fmtMoney, fmtNumber } from "./reward-shared-meta";

/** Thứ tự tab — 'all' trước, rồi theo vòng đời đợt. */
export const STATUS_TABS = ["all", "running", "scheduled", "draft", "ended", "settled"];

/** Đếm số đợt theo từng trạng thái để gắn lên nhãn tab. */
export const countByStatus = (programs) =>
    programs.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), { all: programs.length });

/** Giá trị lọc cho đợt KHÔNG gắn campId (nhiều sàn — chi tiết nằm ở config.camps). */
export const MULTI_CAMP = "__multi__";

/** Lọc theo tab + từ khoá (tên/slug/id) + loại + sàn + chỉ đợt đang bật. */
export const filterPrograms = (programs, { status = "all", keyword = "", type, camp, enabledOnly } = {}) => {
    const kw = keyword.trim().toLowerCase();
    return programs.filter((p) => {
        if (status !== "all" && p.status !== status) return false;
        if (type && p.type !== type) return false;
        if (camp && (camp === MULTI_CAMP ? p.campId : p.campId !== camp)) return false;
        if (enabledOnly && !p.enabled) return false;
        if (!kw) return true;
        return `${p.name} ${p.slug} ${p.id}`.toLowerCase().includes(kw);
    });
};

/**
 * Chọn sàn chỉ liệt kê sàn CÓ trong dữ liệu — tránh menu đầy sàn chưa từng chạy đợt.
 * Sàn lạ (chưa có trong CAMP_LABELS) vẫn hiện bằng mã gốc.
 */
const campOptions = (programs) => {
    const camps = [...new Set(programs.map((p) => p.campId).filter(Boolean))];
    const options = camps.map((c) => ({ value: c, label: CAMP_LABELS[c] || c }));
    if (programs.some((p) => !p.campId)) options.push({ value: MULTI_CAMP, label: "Nhiều sàn" });
    return options;
};

/** 1 ô số liệu. */
const StatCard = ({ icon, label, value, sub, tone = "text-content" }) => (
    <div className="flex-1 min-w-[170px] bg-surface border border-line rounded-lg shadow-sm px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-surface-muted flex items-center justify-center text-content-3">
            <FontAwesomeIcon icon={icon} />
        </div>
        <div className="min-w-0">
            <div className="text-xs text-content-3">{label}</div>
            <div className={`text-lg font-semibold leading-6 truncate ${tone}`}>{value}</div>
            {sub ? <div className="text-xs text-content-3 truncate">{sub}</div> : null}
        </div>
    </div>
);

/** Hàng thẻ số liệu — tính trên TOÀN BỘ đợt, không đổi theo tab đang xem. */
export function RewardProgramStats({ programs }) {
    const running = programs.filter((p) => p.status === "running").length;
    const totalBudget = programs.reduce((s, p) => s + Number(p.totalBudget || 0), 0);
    const usedBudget = programs.reduce((s, p) => s + Number(p.usedBudget || 0), 0);
    const participants = programs.reduce((s, p) => s + Number(p.participantsCount || 0), 0);
    return (
        <div className="flex flex-wrap gap-3">
            <StatCard icon={faLayerGroup} label="Tổng số đợt" value={fmtNumber(programs.length)} sub={`${programs.filter((p) => p.enabled).length} đợt đang bật`} />
            <StatCard icon={faBolt} label="Đang chạy" value={fmtNumber(running)} tone="text-green-600" sub={`${countByStatus(programs).scheduled || 0} đợt chờ tới ngày`} />
            <StatCard icon={faSackDollar} label="Ngân sách đã dùng" value={fmtMoney(usedBudget)} sub={totalBudget ? `trên ${fmtMoney(totalBudget)} đã cấp` : "chưa đợt nào đặt trần"} />
            <StatCard icon={faUsers} label="Lượt đăng ký" value={fmtNumber(participants)} sub="cộng dồn mọi đợt" />
        </div>
    );
}

/** Tab trạng thái + lọc loại/sàn/đang bật + tìm kiếm. */
export function RewardProgramFilters({
    programs, countPool, status, onStatusChange, keyword, onKeywordChange,
    type, onTypeChange, camp, onCampChange, enabledOnly, onEnabledOnlyChange,
    typeLabels, showing,
}) {
    // Đếm trên tập đã lọc sàn/loại/bật-tắt (countPool) — số trên tab phải khớp
    // đúng số dòng sẽ thấy khi bấm vào, không phải tổng toàn bộ.
    const counts = countByStatus(countPool);
    const options = STATUS_TABS.map((key) => ({
        value: key,
        label: (
            <span>
                {key === "all" ? "Tất cả" : PROGRAM_STATUS_META[key]?.label || key}
                <span className="ml-1.5 text-xs text-content-3">{counts[key] || 0}</span>
            </span>
        ),
    }));
    return (
        <div className="flex flex-wrap items-center gap-3">
            <Segmented value={status} onChange={onStatusChange} options={options} />
            <div className="flex-1" />
            <Tooltip title="Ẩn đợt đang tắt (nháp, đã dừng)">
                <span className="flex items-center gap-2 text-xs text-content-3 whitespace-nowrap">
                    <Switch size="small" checked={enabledOnly} onChange={onEnabledOnlyChange} />
                    Chỉ đợt đang bật
                </span>
            </Tooltip>
            <Select
                allowClear placeholder="Mọi sàn" value={camp || undefined} onChange={(v) => onCampChange(v || "")}
                style={{ minWidth: 130 }} options={campOptions(programs)}
            />
            <Select
                allowClear placeholder="Mọi loại chương trình" value={type || undefined} onChange={(v) => onTypeChange(v || "")}
                style={{ minWidth: 190 }}
                options={Object.entries(typeLabels).map(([value, label]) => ({ value, label }))}
            />
            <Input.Search
                allowClear placeholder="Tìm tên, slug hoặc ID" value={keyword}
                onChange={(e) => onKeywordChange(e.target.value)} style={{ width: 220 }}
            />
            <Tooltip title="Số đợt khớp bộ lọc hiện tại">
                <span className="text-xs text-content-3 whitespace-nowrap">{fmtNumber(showing)} đợt</span>
            </Tooltip>
        </div>
    );
}
